import { amountToDisplayValue, InternalAccessor } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Contact } from '@features/contacts/types/contact'
import { InvoiceState } from '@features/invoicesLegacy/enums/invoiceState'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { getInvoiceStatus } from '@features/invoicesLegacy/utils/getInvoiceStatus'

import { EmberRoute } from '../../../../../enums/emberRoute'
import { useEmberRouteToUrlGenerator } from '../../../../../hooks/routing/useEmberRouteToUrlGenerator'
import { getContactName } from '../../../../contacts/utils/getContactName'
import { getInvoicesTableStaticActions } from '../utils/getInvoicesStaticActions'
import { InvoicesTableAccessor } from './../enums/invoicesTableAccessor'
import { InvoicesTableRow } from './../types/invoicesTableRow'

export const useInvoicesTableData = (invoices: Invoice[], contacts: Contact[]): InvoicesTableRow[] => {
  const { t } = useTranslation()
  const { generateUrl } = useEmberRouteToUrlGenerator()

  const generateInvoiceUrl = useMemo(() => {
    if (!generateUrl) {
      return () => '#'
    }

    return (route: EmberRoute, routeId?: string) => {
      switch (route) {
        case EmberRoute.InvoiceCreditNote:
          return generateUrl(route, routeId)
        case EmberRoute.InvoicesNew:
          return `${generateUrl(route)}?from_invoice_id=${routeId}`
      }
    }
  }, [generateUrl])

  return useMemo(
    () =>
      invoices.map((invoice) => {
        const {
          amount,
          balance,
          contactId,
          currencyId,
          dueDate,
          entryDate,
          grossAmount,
          id,
          invoiceNo,
          isPaid,
          lineDescription,
          orderNo,
          referredCreditnoteId,
          sentState,
          state,
          tax,
          type,
        } = invoice
        const isCreditNote = type === InvoiceType.CreditNote
        const isApproved = state === InvoiceState.Approved
        const hasHoverActions = isApproved && !isPaid
        const createCreditNoteUrl = generateInvoiceUrl(EmberRoute.InvoiceCreditNote, id) || ''
        const duplicateInvoiceUrl = generateInvoiceUrl(EmberRoute.InvoicesNew, id) || ''

        const staticActionsItems = getInvoicesTableStaticActions({
          createCreditNoteUrl,
          duplicateInvoiceUrl,
          canRegisterPayment: !isPaid,
          t,
        })

        return {
          [InvoicesTableAccessor.Date]: entryDate,
          [InvoicesTableAccessor.Status]: getInvoiceStatus({
            amount,
            balance,
            dueDate: dueDate ? new Date(dueDate) : undefined,
            isPaid,
            referredCreditnoteId,
            state,
            tax,
          }),
          [InvoicesTableAccessor.DueDate]: state === InvoiceState.Approved && !isPaid ? dueDate : '',
          [InvoicesTableAccessor.EmailStatus]: sentState,
          [InvoicesTableAccessor.Customer]: getContactName(contacts, contactId),
          [InvoicesTableAccessor.OrderNumber]: orderNo || '',
          [InvoicesTableAccessor.Description]: lineDescription
            ? {
                isCreditNote,
                value: lineDescription,
              }
            : undefined,
          [InvoicesTableAccessor.IncludedVat]:
            grossAmount && currencyId ? `${amountToDisplayValue(grossAmount)} ${currencyId}` : undefined,
          [InvoicesTableAccessor.ExcludedVat]:
            amount && currencyId ? `${amountToDisplayValue(amount)} ${currencyId}` : undefined,
          [InvoicesTableAccessor.Balance]: currencyId
            ? `${amountToDisplayValue(balance || 0)} ${currencyId}`
            : undefined,
          [InvoicesTableAccessor.InvoiceNo]: invoiceNo,
          [InternalAccessor.HoverActions]: hasHoverActions,
          [InternalAccessor.StaticActions]: isApproved,
          id,
          staticActionsItems,
          isCreditNote,
          invoice,
        }
      }),
    [invoices, generateInvoiceUrl, t, contacts],
  )
}
